import { Stack, Text, useTheme } from 'tamagui'
import Svg, { Circle } from 'react-native-svg'
import { memo, useEffect, useMemo, useRef } from 'react'
import { Animated, Easing } from 'react-native'
import { useThemeColor } from '@hooks/useThemeColor'

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

interface IProgressCircleProps {
  progress: number
  size?: number
  strokeWidth?: number
  backgroundColorToken?: string
  progressColorToken?: string
  duration?: number
  delay?: number
  /** Quando true, mostra a porcentagem centralizada dentro do círculo. */
  showPercentageLabel?: boolean
  labelColorToken?: string
}

function ProgressCircleComponent({
  progress,
  size = 40,
  strokeWidth = 4,
  backgroundColorToken = '$progress-track',
  progressColorToken = '$progress-fill',
  duration = 1000,
  delay = 300,
  showPercentageLabel = false,
  labelColorToken = '$text-secondary',
}: IProgressCircleProps) {
  const theme = useTheme()
  const trackColor = useThemeColor('progress-track')
  const fillColor = useThemeColor('progress-fill')
  const normalizedProgress = Math.min(Math.max(progress, 0), 100)
  const animatedValue = useRef(new Animated.Value(0)).current

  const backgroundColorValue = theme[backgroundColorToken]?.val || trackColor
  const progressColorValue = theme[progressColorToken]?.val || fillColor

  const radius = size / 2 - strokeWidth
  const center = size / 2
  const circumference = 2 * Math.PI * radius

  useEffect(() => {
    const animation = Animated.sequence([
      Animated.delay(delay),
      Animated.timing(animatedValue, {
        toValue: normalizedProgress,
        duration,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: false,
      }),
    ])

    animation.start()

    return () => {
      animation.stop()
    }
  }, [normalizedProgress, duration, delay, animatedValue])

  const strokeDashoffset = useMemo(
    () =>
      animatedValue.interpolate({
        inputRange: [0, 100],
        outputRange: [circumference, 0],
        extrapolate: 'clamp',
      }),
    [animatedValue, circumference],
  )

  return (
    <Stack
      width={size}
      height={size}
      alignItems="center"
      justifyContent="center"
      testID="progress-circle"
    >
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <Circle
          testID="progress-circle-background"
          cx={center}
          cy={center}
          r={radius}
          stroke={backgroundColorValue}
          strokeWidth={strokeWidth}
          opacity={0.4}
          fill="none"
        />

        <AnimatedCircle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColorValue}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90, ${center}, ${center})`}
        />
      </Svg>

      {showPercentageLabel && (
        <Text
          position="absolute"
          color={labelColorToken}
          fontFamily="$mulishBold"
          fontWeight="700"
          fontSize={Math.max(10, Math.round(size * 0.22))}
        >
          {Math.round(normalizedProgress)}%
        </Text>
      )}
    </Stack>
  )
}

export const ProgressCircle = memo(ProgressCircleComponent)
